import { useEffect, useRef, useState } from "react";
import { API, CATEGORIES, catLabel, rupiah, errMsg, uploadFile, imgSrc, imageRatioClass, imagePositionClass, normalizeImageRatio, normalizeImagePosition } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ImagePlus } from "lucide-react";
import ImageRatioPicker from "@/components/ImageRatioPicker";
import ImagePositionPicker from "@/components/ImagePositionPicker";

const EMPTY = { name: "", category: "tali", brand: "", description: "", unit: "Kilogram", images: [], image_ratios: [], image_positions: [], video_url: "", variants: [], is_active: true, sort_order: 999 };

export default function Products() {
  const [products, setProducts] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} product form
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const varFileRef = useRef(null);
  const [varImgIdx, setVarImgIdx] = useState(null);
  const [varUploading, setVarUploading] = useState(false);

  const load = () => API.get("/products", { params: { all: true } }).then((r) => setProducts(r.data)).catch(() => setProducts([]));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ ...EMPTY, variants: [{ label: "", price: 0, stock: 0, price_kg: 0, stock_kg: 0, price_roll: 0, stock_roll: 0, image: "", image_ratio: "1:1", image_position: "center" }] }); setEditId(null); };
  const openEdit = (p) => {
    const images = p.images || [];
    setEditing({ name: p.name, category: p.category, brand: p.brand || "", description: p.description || "", unit: p.unit || "pcs", images, image_ratios: images.map((_, i) => normalizeImageRatio(p.image_ratios?.[i], "1:1")), image_positions: images.map((_, i) => normalizeImagePosition(p.image_positions?.[i])), video_url: p.video_url || "", variants: (p.variants || []).map((v) => ({ label: v.label, price: v.price, stock: v.stock, price_kg: v.price_kg || 0, stock_kg: v.stock_kg || 0, price_roll: v.price_roll || 0, stock_roll: v.stock_roll || 0, image: v.image || "", image_ratio: normalizeImageRatio(v.image_ratio, "1:1"), image_position: normalizeImagePosition(v.image_position) })), is_active: p.is_active, sort_order: p.sort_order ?? 999 });
    setEditId(p.id);
  };

  const pickVariantImage = async (e) => {
    const file = e.target.files[0];
    if (!file || varImgIdx === null) return;
    setVarUploading(true);
    try {
      const url = await uploadFile(file);
      setEditing((ed) => {
        const vs = [...ed.variants];
        vs[varImgIdx] = { ...vs[varImgIdx], image: url, image_ratio: normalizeImageRatio(vs[varImgIdx].image_ratio, "1:1"), image_position: normalizeImagePosition(vs[varImgIdx].image_position) };
        return { ...ed, variants: vs };
      });
      toast.success("Foto varian terunggah");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setVarUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    const payload = { ...editing, variants: editing.variants.filter((v) => v.label).map((v) => ({ ...v, price: Number(v.price) || 0, stock: Number(v.stock) || 0, price_kg: editing.category === "tali" ? Number(v.price_kg) || 0 : 0, stock_kg: editing.category === "tali" ? Number(v.stock_kg) || 0 : 0, price_roll: editing.category === "tali" ? Number(v.price_roll) || 0 : 0, stock_roll: editing.category === "tali" ? Number(v.stock_roll) || 0 : 0 })) };
    try {
      if (editId) await API.put(`/products/${editId}`, payload);
      else await API.post("/products", payload);
      toast.success("Produk disimpan");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus produk ini?")) return;
    try {
      await API.delete(`/products/${id}`);
      toast.success("Produk dihapus");
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const pickImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setEditing((ed) => ({ ...ed, images: [...ed.images, url], image_ratios: [...(ed.image_ratios || []), "1:1"], image_positions: [...(ed.image_positions || []), "center"] }));
      toast.success("Gambar terunggah");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";

  return (
    <div data-testid="admin-products">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Produk</h1>
          <p className="text-sm text-slate-500">Kelola etalase, harga, dan gambar produk</p>
        </div>
        <button onClick={openNew} data-testid="add-product" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
          <Plus className="h-4 w-4" /> Tambah Produk
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(products || []).map((p) => (
          <div key={p.id} data-testid={`product-card-${p.id}`} className="bg-white border border-slate-200 rounded-md overflow-hidden">
            <div className={`${imageRatioClass(p.image_ratios?.[0], "1:1")} bg-slate-100 relative`}>
              <img src={imgSrc((p.images || [])[0])} alt={p.name} className={`w-full h-full object-cover ${imagePositionClass(p.image_positions?.[0])}`} />
              {!p.is_active && <span className="absolute top-2 left-2 bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded">NONAKTIF</span>}
            </div>
            <div className="p-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-[#C9A227]">{p.brand || "-"} • {catLabel(p.category)}</p>
              <p className="font-bold text-sm mt-1">{p.name}</p>
              <p className="text-xs text-slate-500 mt-1">{(p.variants || []).length} varian • mulai {p.variants?.length ? rupiah(Math.min(...p.variants.map((v) => v.price))) : "-"} • urutan {p.sort_order ?? 0}</p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-300 text-xs font-bold rounded-md hover:border-[#0B1F3A] transition-colors">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => remove(p.id)} data-testid={`delete-product-${p.id}`} className="px-3 py-2 border border-red-300 text-red-600 text-xs font-bold rounded-md hover:bg-red-50 transition-colors">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {products && products.length === 0 && <p className="text-slate-400 col-span-full py-10 text-center">Belum ada produk.</p>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setEditing(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" data-testid="product-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Produk" : "Tambah Produk"}</h2>
              <button onClick={() => setEditing(null)} data-testid="product-dialog-close"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className={labelCls}>Nama Produk *</label>
                <input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="product-name-input" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Kategori</label>
                <select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} data-testid="product-category-input" className={inputCls}>
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Merek</label>
                <input value={editing.brand} onChange={(e) => setEditing({ ...editing, brand: e.target.value })} data-testid="product-brand-input" className={inputCls} placeholder="LUMBA LUMBA / KUDA EMAS" />
              </div>
              <div>
                <label className={labelCls}>Satuan</label>
                <input value={editing.unit} onChange={(e) => setEditing({ ...editing, unit: e.target.value })} data-testid="product-unit-input" className={inputCls} placeholder="roll / pcs / unit" />
              </div>
              <div>
                <label className={labelCls}>Urutan Tampil</label>
                <input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: Number(e.target.value) || 0 })} data-testid="product-sort-input" className={inputCls} placeholder="0" />
                <p className="text-[10px] text-slate-400 mt-1">Angka lebih kecil tampil lebih dulu di toko & katalog.</p>
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} data-testid="product-active-input" className="h-4 w-4 accent-[#0B1F3A]" />
                  Tampilkan di etalase
                </label>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Deskripsi</label>
                <textarea rows={3} value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="product-desc-input" className={inputCls} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Video Penjelasan Produk (URL YouTube / mp4, opsional)</label>
                <input value={editing.video_url} onChange={(e) => setEditing({ ...editing, video_url: e.target.value })} data-testid="product-video-input" className={inputCls} placeholder="https://youtube.com/watch?v=..." />
                <p className="text-xs text-slate-400 mt-1.5">Video tampil di halaman detail produk toko online.</p>
              </div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Gambar Produk</label>
                <p className="text-[10px] text-slate-400 mb-2">Setiap foto dapat memakai rasio berbeda. Pilihan tidak mengubah file asli dan dapat diganti kapan saja.</p>
                <div className="flex flex-wrap gap-3 items-start">
                  {editing.images.map((im, i) => (
                    <div key={i} className="w-32 rounded-md border border-slate-200 bg-slate-50 p-2">
                      <div className={`relative w-full rounded overflow-hidden bg-white ${imageRatioClass(editing.image_ratios?.[i], "1:1")}`}>
                        <img src={imgSrc(im)} alt={`Foto produk ${i + 1}`} className={`w-full h-full object-cover ${imagePositionClass(editing.image_positions?.[i])}`} />
                        <button
                          type="button"
                          onClick={() => setEditing({ ...editing, images: editing.images.filter((_, x) => x !== i), image_ratios: (editing.image_ratios || []).filter((_, x) => x !== i), image_positions: (editing.image_positions || []).filter((_, x) => x !== i) })}
                          data-testid={`remove-image-${i}`}
                          className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                      <ImageRatioPicker
                        value={editing.image_ratios?.[i]}
                        onChange={(value) => { const ratios = [...(editing.image_ratios || [])]; ratios[i] = value; setEditing({ ...editing, image_ratios: ratios }); }}
                        defaultRatio="1:1"
                        testid={`product-image-ratio-${i}`}
                        compact
                      />
                      <ImagePositionPicker
                        value={editing.image_positions?.[i]}
                        onChange={(value) => { const positions = [...(editing.image_positions || [])]; positions[i] = value; setEditing({ ...editing, image_positions: positions }); }}
                        testid={`product-image-position-${i}`}
                        compact
                      />
                    </div>
                  ))}
                  <input type="file" accept="image/*" ref={fileRef} className="hidden" onChange={pickImage} data-testid="product-image-input" />
                  <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-image-button" className="h-20 w-20 border-2 border-dashed border-slate-300 rounded-md flex flex-col items-center justify-center text-slate-400 hover:border-[#C9A227] hover:text-[#C9A227] transition-colors">
                    <ImagePlus className="h-5 w-5" />
                    <span className="text-[10px] mt-1">{uploading ? "..." : "Unggah"}</span>
                  </button>
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="flex items-center justify-between mb-2">
                  <label className={labelCls + " mb-0"}>Varian (ukuran/kapasitas, harga, stok, foto per varian)</label>
                  <button onClick={() => setEditing({ ...editing, variants: [...editing.variants, { label: "", price: 0, stock: 0, price_kg: 0, stock_kg: 0, price_roll: 0, stock_roll: 0, image: "", image_ratio: "1:1", image_position: "center" }] })} data-testid="add-variant" className="text-xs font-bold text-[#C9A227] hover:underline">
                    + Tambah Varian
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mb-2">Setiap varian bisa punya foto sendiri — klik ikon gambar di baris varian untuk upload. Foto tampil otomatis saat pelanggan memilih varian di toko.</p>
                <input ref={varFileRef} type="file" accept="image/*" className="hidden" onChange={pickVariantImage} data-testid="variant-image-input" />
                <div className="space-y-2">
                  {editing.variants.map((v, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`variant-row-${i}`}>
                      <input value={v.label} onChange={(e) => { const vs = [...editing.variants]; vs[i] = { ...v, label: e.target.value }; setEditing({ ...editing, variants: vs }); }} data-testid={`variant-label-${i}`} className={inputCls + " col-span-3"} placeholder="Mis: 8 mm" />
                      <input type="number" min="0" value={v.price} onChange={(e) => { const vs = [...editing.variants]; vs[i] = { ...v, price: e.target.value }; setEditing({ ...editing, variants: vs }); }} data-testid={`variant-price-${i}`} className={inputCls + " col-span-3"} placeholder="Harga" />
                      <input type="number" min="0" value={v.stock} onChange={(e) => { const vs = [...editing.variants]; vs[i] = { ...v, stock: e.target.value }; setEditing({ ...editing, variants: vs }); }} data-testid={`variant-stock-${i}`} className={inputCls + " col-span-2"} placeholder="Stok" />
                      <div className="col-span-3">
                        <button
                          type="button"
                          onClick={() => { setVarImgIdx(i); varFileRef.current?.click(); }}
                          disabled={varUploading}
                          data-testid={`variant-image-upload-${i}`}
                          title={v.image ? "Ganti foto varian" : "Upload foto varian"}
                          className={`w-full h-10 rounded-md border flex items-center justify-center gap-1.5 overflow-hidden transition-colors ${v.image ? "border-[#C9A227]" : "border-dashed border-slate-300 text-slate-400 hover:border-[#C9A227] hover:text-[#C9A227]"}`}
                        >
                          {v.image ? (
                            <img src={imgSrc(v.image)} alt={`Foto ${v.label || "varian"}`} className={`h-full w-full object-cover ${imagePositionClass(v.image_position)}`} />
                          ) : (
                            <><ImagePlus className="h-4 w-4" /><span className="text-[10px] font-bold">{varUploading && varImgIdx === i ? "..." : "Foto"}</span></>
                          )}
                        </button>
                        {v.image && (
                          <>
                            <ImageRatioPicker
                              value={v.image_ratio}
                              onChange={(value) => { const vs = [...editing.variants]; vs[i] = { ...v, image_ratio: value }; setEditing({ ...editing, variants: vs }); }}
                              defaultRatio="1:1"
                              testid={`variant-image-ratio-${i}`}
                              compact
                            />
                            <ImagePositionPicker
                              value={v.image_position}
                              onChange={(value) => { const vs = [...editing.variants]; vs[i] = { ...v, image_position: value }; setEditing({ ...editing, variants: vs }); }}
                              testid={`variant-image-position-${i}`}
                              compact
                            />
                          </>
                        )}
                      </div>
                      <button onClick={() => setEditing({ ...editing, variants: editing.variants.filter((_, x) => x !== i) })} data-testid={`variant-remove-${i}`} className="col-span-1 text-red-500 hover:text-red-700">
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {editing.category === "tali" && (
                        <div className="col-span-11 grid grid-cols-2 gap-2 rounded-md border border-amber-200 bg-amber-50 p-2">
                          <div>
                            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-amber-700">Harga per Roll</label>
                            <input type="number" min="0" value={v.price_roll || ""} onChange={(e) => { const vs = [...editing.variants]; vs[i] = { ...v, price_roll: e.target.value }; setEditing({ ...editing, variants: vs }); }} data-testid={`variant-price-roll-${i}`} className={inputCls} placeholder="Harga / roll" />
                          </div>
                          <div>
                            <label className="mb-1 block text-[10px] font-bold uppercase tracking-wider text-amber-700">Stok Roll</label>
                            <input type="number" min="0" step="0.01" value={v.stock_roll || ""} onChange={(e) => { const vs = [...editing.variants]; vs[i] = { ...v, stock_roll: e.target.value }; setEditing({ ...editing, variants: vs }); }} data-testid={`variant-stock-roll-${i}`} className={inputCls} placeholder="Stok roll" />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <button onClick={save} disabled={!editing.name} data-testid="save-product" className="mt-6 w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
              Simpan Produk
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
